import React, { useContext, useState } from 'react';
import MuiTooltip from '../MuiComponents/MuiTooltip';
import ReactSelectSingleSelect from './ReactSelectSingleSelect';
import { AlertContext } from '../SuccessOrFailedModal';
import MainLoader from '../Loader/MainLoader';

const GenericDropDown = ({
    className,
    width,
    placeholder,
    borderColor = "var(--primary-500)",
    border,
    options = [],
    value,
    menuIsOpen,
    components,
    renderStyles = true,
    placeholderColor = 'var(--primary-800)',
    menuBorderRadius = 8,
    backgroundColor = '',
    showPopup = false,
    onChange,
    isLoading = false,
    tooltipPlacement = "right",
    formatOptionLabel,
    onUpdateCallback,
    showUpdateConfirmation = false,
    updateConfirmationProps = {
        title: "Are you sure?",
        variant: "",
        description: "This value will be updated.",
        okayText: "Yes",
        cancelText: "No"
    }
}) => {
    const defaultOption = value ? options.find(option => option.value === value) : null;
    const [selectedValue, setSelectedValue] = useState(value);
    const [selectedOption, setSelectedOption] = useState(defaultOption);
    const [popup] = useContext(AlertContext);

    // Default format for option label if not provided
    const defaultFormatOptionLabel = (option) => {
        if (option.tooltip) {
            return (
                <MuiTooltip title={
                    <div style={{ whiteSpace: 'pre-line' }}>
                        {typeof option.tooltip === 'string' ? option.tooltip : 
                            Array.isArray(option.tooltip) ? 
                                option.tooltip.map((item, index) => (
                                    <li key={index}>{item}</li>
                                )) : 
                                option.tooltip
                        }
                    </div>} 
                    placement={tooltipPlacement}
                >
                    <span style={{ paddingRight: "5px" }}>{option.label}</span>
                </MuiTooltip>
            );
        }
        return option.label;
    };

    const handleChange = async (option) => {
        setSelectedValue(option.value);
        setSelectedOption(option);

        const updateValue = async () => {
            if (onUpdateCallback) {
                await onUpdateCallback(option);
            }
            if (onChange) {
                onChange(option);
            }
        };

        if (showUpdateConfirmation && showPopup) {
            popup(updateConfirmationProps).then(updateValue);
        } else {
            updateValue();
        }
    };

    const colourStyles = {
        indicatorSeparator: () => ({ display: 'none' }),
        dropdownIndicator: base => ({
            ...base,
            color: "var(--primary-800)"
        }),
        menu: base => ({
            ...base,
            zIndex: 99,
            borderRadius: menuBorderRadius,
            marginTop: 0,
        }),
        menuList: base => ({
            ...base,
            padding: 0,
        }),
        control: (base, state) => ({
            ...base,
            border,
            borderRadius: 8,
            borderColor,
            boxShadow: state.isFocused ? null : null,
            backgroundColor,
            "&:hover": {
                borderColor: borderColor,
            },
            width,
        }),
        option: (provided, state) => ({
            ...provided,
            fontSize: '14px',
            backgroundColor: state.isSelected ? "var(--primary-500)" : "white",
            "&:hover": {
                backgroundColor: state.isSelected ? "var(--primary-300)" : "var(--background)",
            },
            "& span": {
                display: 'block',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
            },
            "&:hover span": {
                overflow: 'visible',
                whiteSpace: 'normal',
            },
            fontWeight: 400,
            letterSpacing: '1px',
        }),
        placeholder: (provided) => ({
            ...provided,
            fontSize: '16px',
            color: placeholderColor,
            fontWeight: renderStyles ? 600 : null,
            letterSpacing: renderStyles ? '0.15px' : null
        }),
        singleValue: (provided) => ({
            ...provided,
            fontSize: '16px',
            fontWeight: renderStyles ? 600 : null,
            letterSpacing: renderStyles ? '0.15px' : null
        }),
    };

    return (
        <div>
            {isLoading && <MainLoader />}
            <ReactSelectSingleSelect
                name={'generic-dropdown'}
                className={`${className}`}
                options={options}
                styles={colourStyles}
                placeholder={placeholder}
                onChange={handleChange}
                isSearchable={false}
                value={selectedOption}
                menuIsOpen={menuIsOpen}
                components={components}
                formatOptionLabel={formatOptionLabel || defaultFormatOptionLabel}
            />
        </div>
    );
};

export default GenericDropDown;